<template>
    <div
        class="shopping-cart container"
        :class="{
            'bg-white': loaded,
            'p-4' : isStandalone,
            'p-3 backend' : !isStandalone
        }"
    >
        <googleRecaptcha v-if="isStandalone" ref="googleRecaptcha" @scriptLoaded="googleScriptLoaded" />
        <template v-if="loaded">
            <div class="row gx-3">
                <div class="col-12">
                    <!-- Cart Items Table -->
                    <div v-if="cartItems.length > 0" class="cart-items">
                        <div class="title-label mb-3">
                            <div class="h4 mb-0 text-black">Shopping Cart</div>
                            <div>Review {{ isStandalone ? 'your' : 'customer' }} booking.</div>
                        </div>
                        <table class="table cart-list">
                            <thead>
                            <tr>
                                <th :class="{'text-white': !isStandalone}">Item</th>
                                <th :class="{'text-white': !isStandalone}" class="text-end d-none"></th>
                                <th :class="{'text-white': !isStandalone}" class="text-end">Quantity</th>
                                <th :class="{'text-white': !isStandalone}" class="text-end">Price</th>
                                <th :class="{'text-white': !isStandalone}"></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="item in cartItems" :key="item.id">
                                <td>
                                    <div>
                                        <div class="item-destination">{{ item.destinations }}</div>
                                        <div
                                            class="item-name"
                                            :class="{'text-decoration-line-through': !item.available}"
                                        >
                                            {{ item.name }}
                                        </div>
                                        <div class="item-description" :class="{'text-decoration-line-through': !item.available}">{{ item.description }}</div>
                                        <div v-if="item.sub_description && !Array.isArray(item.sub_description)" class="item-sub-desc" :class="{'text-decoration-line-through': !item.available}">{{ item.sub_description }}</div>
                                        <div v-else-if="item.sub_description && Array.isArray(item.sub_description) && item.sub_description.length" class="item-sub-desc">
                                            <span :class="{'text-decoration-line-through': !item.available}" v-for="sub_desc in item.sub_description">{{ sub_desc }}</span>
                                        </div>
                                        <div v-if="item.notes && Array.isArray(item.notes) && item.notes.length" class="item-sub-desc">
                                            <span v-for="sub_desc in item.notes">{{ sub_desc }}</span>
                                        </div>
                                    </div>
                                    <div v-if="item.promocode" class="text-danger" :class="{'text-decoration-line-through': !item.available}" >
                                        Promo Code ({{ item.promocode }})
                                    </div>
                                </td>
                                <td class="d-none">
                                    <div class="item-price"></div>
                                </td>
                                <td>
                                    <div class="item-quantity">
                                        {{ item.quantity }}
                                    </div>
                                </td>
                                <td style="line-height: 1.2;">
                                    <div
                                        v-if="item.promocode"
                                        class="item-price small"
                                        :class="{'text-decoration-line-through': !item.available}"
                                    >
                                        {{ formatPrice(item.before_discount, item.currency.symbol) }}
                                    </div>
                                    <div
                                        v-if="item.promocode"
                                        class="item-price small text-secondary"
                                        :class="{'text-decoration-line-through': !item.available}"
                                    >
                                        -{{ formatPrice(item.discount, item.currency.symbol) }}
                                    </div>
                                    <div
                                        class="item-price"
                                        :class="{'text-decoration-line-through': !item.available}"
                                    >
                                        {{ formatPrice(item.price, item.currency.symbol) }}
                                    </div>
                                </td>
                                <td class="item-actions">
                                    <button
                                        type="button"
                                        class="btn-edit me-1"
                                        title="Edit item"
                                        @click="editItem(item)"
                                        :disabled="!item.available && isStandalone"
                                    >
                                        <i class="ti ti-edit"></i><span class="text">Edit</span>
                                    </button>
                                    <button
                                        type="button"
                                        @click="removeItem($event, item.id)"
                                        class="btn-remove"
                                        title="Remove item"
                                    >
                                        <i class="ti ti-x"></i><span class="text">Remove</span>
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <!-- Cart Summary -->
                        <div class="cart-summary">
                            <div class="summary-row">
                                <span class="summary-label">Subtotal:</span>
                                <span class="summary-value">{{ formatPrice(subTotalPrice, currency?.symbol) }}</span>
                            </div>
                            <div v-if="discount" class="summary-row">
                                <span class="summary-label">Promo Code:</span>
                                <span class="summary-value text-secondary small">-{{ formatPrice(discount, currency?.symbol) }}</span>
                            </div>
                            <div class="summary-row">
                                <span class="summary-label">Total:</span>
                                <span class="summary-value">{{ formatPrice(totalPrice, currency?.symbol) }}</span>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="cart-actions mt-3">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <a v-if="isStandalone" class="btn btn-outline-secondary btn_1 me-2" href="/tours">
                                        Continue Shopping
                                    </a>
                                    <button
                                        v-if="totalPrice"
                                        type="button"
                                        class="btn btn-primary btn_1 full-width purchase"
                                        @click="proceedToCheckout"
                                    >
                                        Proceed to Checkout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty Cart State -->
                    <div v-else class="empty-cart">
                        <div class="empty-icon">
                            <i class="ti ti-basket"></i>
                        </div>
                        <template v-if="isStandalone">
                            <div class="empty-message">Your cart is empty</div>
                            <div class="empty-description">Add some items to your cart to get started</div>
                            <a class="btn btn-primary btn_1 mt-3" href="/tours">
                                <i class="ti ti-basket me-1"></i>
                                Start Shopping
                            </a>
                        </template>
                        <template v-else>
                            <div class="empty-message">Shopping cart is empty</div>
                            <div class="empty-description">Add some items to shopping cart to get started</div>
                        </template>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import googleRecaptcha from "../../../../components/form/googleRecaptcha.vue";
import {gid, setgeo} from "../../../../utils/webRequest.js";
import {utilHelper} from "../../../../utils/helpers/utilHelper.js";
import {elementHelper} from "../../../../utils/helpers/elementHelper.js";
import ApiService from "../../../../services/api.js";
import {errorHelper} from "../../../../utils/helpers/errorHelper.js";
import AjxService from "../../../../services/ajax.js";
import {formatHelper} from "../../../../utils/helpers/formatHelper.js";
import {objectHelper} from "../../../../utils/helpers/objectHelper.js";

export default {
    name: "shoppingCart",
    components: {googleRecaptcha},
    emits:[
        'changeBooking',
        'goInvoice'
    ],
    props: {
        isStandalone: {
            type: Boolean,
            default: false
        },
        shoppingCartId: {
            type: [null, String],
            default: null
        }
    },
    data() {
        return {
            isMounted: false,
            isGoogleScriptLoaded: false,
            loaded: false,
            cartItems: [],
            discount: 0,
            tax: 0,
            subTotalPrice: 0,
            totalPrice: 0,
            currency: {
                name: null,
                code: null,
                symbol: null
            },
        }
    },
    computed: {
        loggedIn() {
            return this.$store.getters["auth/loggedIn"];
        }
    },
    mounted() {
        this.isMounted = true;
        this.$nextTick(() => {
            this.setActivated();
            this.isMounted = false;
        });
    },
    activated() {
        if(!this.isMounted) {
            this.setActivated();
        }
    },
    deactivated() {
        this.setDeactivated();
    },
    unmounted() {
        this.setDeactivated();
    },
    methods: {
        setActivated() {
            this.loaded = false;
            if (this.isGoogleScriptLoaded || !this.isStandalone) {
                this.getShoppingCartItems();
            }
        },
        setDeactivated() {
            this.loaded = false;
        },
        formatPrice(number, symbol = '') {
            const price = (typeof (number || 0)) === 'number' ? (number || 0) : (!isNaN(parseFloat(number)) ? parseFloat(number) : 0);
            if(price) {
                return symbol + formatHelper.formatNumber(price, (symbol.trim().toLowerCase()==='rp'?'id-ID':'en-US'));
            }
            return '-';
        },
        async removeItem(event, itemId) {
            const index = this.cartItems.findIndex(item => item.id === itemId);
            // this.cartItems.splice(index, 1);

            if (index > -1) {
                await this.getShoppingCartItems({ delete: itemId }, true)
            }
        },
        async proceedToCheckout() {
            if (this.totalPrice) {
                const endpoint = `/booking/shopping-cart/checkout` + (this.shoppingCartId ? `/${this.shoppingCartId}` : ``);
                const loaderId = utilHelper.getUniqueId();
                let element = null;
                let payload = {};

                if (!this.isStandalone) {
                    element = this.$el.closest('.modal-content');
                    element.classList.add('position-relative');
                }

                elementHelper.loader.show(loaderId, element, 36, 36, 'dots');
                if (this.loggedIn) {
                    if (!this.isStandalone) {
                        payload.staff = true;
                    }
                    ApiService.post(endpoint, payload).then(response => {
                        if(response.data.hasOwnProperty('payment_id') && response.data.payment_id) {
                            if (this.isStandalone) {
                                window.location.href = `/booking/payment/${response.data.payment_id}`;
                            } else {
                                this.$emit('goInvoice', {paymentId:response.data.payment_id});
                            }
                        }
                    }).catch(error => {
                        if (!error.response?.isCancel && (!error.name === 'AbortError' || !error.name === 'CanceledError') && (!error.message?.includes('aborted') || !error.message?.includes('canceled'))) {
                            errorHelper.error(error);
                        }
                    }).finally(() => {
                        elementHelper.loader.hide(loaderId);
                        if (element) {
                            element.classList.remove('position-relative');
                        }
                    });
                } else {
                    await this.$refs.googleRecaptcha.verify().then(async token => {
                        payload.token = token;
                        AjxService.post(endpoint, payload).then(response => {
                            if(response.data.hasOwnProperty('payment_id') && response.data.payment_id) {
                                window.location.href = `/booking/payment/${response.data.payment_id}`;
                            }
                        }).catch(error => {
                            if (!error.response?.isCancel && (!error.name === 'AbortError' || !error.name === 'CanceledError') && (!error.message?.includes('aborted') || !error.message?.includes('canceled'))) {
                                errorHelper.error(error);
                            }
                        }).finally(() => {
                            elementHelper.loader.hide(loaderId);
                            if (element) {
                                element.classList.remove('position-relative');
                            }
                        });
                    }).catch(error => {
                        window.location.reload();
                    });
                }
            }
        },
        async googleScriptLoaded() {
            if (this.isStandalone) {
                let geo = await gid();
                if (!geo) {
                    try {
                        const geoData = await setgeo();
                        geo = geoData.i;
                    } catch (error) {
                    }
                }
                if(geo) {
                    const isGuest = window.localStorage.getItem("guest") === "isGuest";
                    if(!isGuest) {
                        await this.$store.dispatch("auth/getAuthUser").then(response => {
                        }).finally(() => {
                            this.getShoppingCartItems();
                        });
                    } else {
                        await this.getShoppingCartItems();
                    }
                }
            } else {
                await this.getShoppingCartItems();
            }
        },
        async getShoppingCartItems(_payload= {}, _loader = false) {
            const loaderId = utilHelper.getUniqueId();
            const endpoint = `/booking/shopping-cart/get` + (this.shoppingCartId ? `/${this.shoppingCartId}` : '');
            let payload = _payload;
            let element = null;

            if (this.loggedIn) {
                if (!this.isStandalone) {
                    payload.staff = true;
                    element = this.$el.closest(_loader ? '.modal-content' : '.modal-body');
                    element.classList.add('position-relative');
                    elementHelper.loader.show(loaderId, this.$el.closest('.modal-body'), 36, 36, 'dots');
                } else if(_loader) {
                    elementHelper.loader.show(loaderId, element, 36, 36, 'dots');
                }
                ApiService.post(endpoint, payload).then(response => {
                    if(!objectHelper.isEmptyObject(response.data)) {
                        this.subTotalPrice = response.data.subTotalPrice;
                        this.totalPrice = response.data.totalPrice;
                        this.discount = response.data.discount;
                        this.currency = response.data.currency;
                        this.cartItems = response.data.items;
                    } else {
                        this.subTotalPrice = 0;
                        this.totalPrice = 0;
                        this.discount = 0;
                        this.cartItems = [];
                        this.currency = {
                            name: null,
                            code: null,
                            symbol: null
                        };
                    }
                }).catch(error => {
                    this.subTotalPrice = 0;
                    this.totalPrice = 0;
                    this.discount = 0;
                    this.cartItems = [];
                    this.currency = {
                        name: null,
                        code: null,
                        symbol: null
                    };
                    if (!error.response?.isCancel && (!error.name === 'AbortError' || !error.name === 'CanceledError') && (!error.message?.includes('aborted') || !error.message?.includes('canceled'))) {
                        errorHelper.error(error);
                    }
                }).finally(() => {
                    if (!this.isStandalone || _loader) {
                        elementHelper.loader.hide(loaderId);
                    }
                    if(element) {
                        element.classList.remove('position-relative');
                    }
                    this.loaded = true;
                });
            } else {
                await this.$refs.googleRecaptcha.verify().then(async token => {
                    payload.token = token;
                    if(_loader) {
                        elementHelper.loader.show(loaderId, this.$el.closest('.modal-body'), 36, 36, 'dots');
                    }
                    AjxService.post(endpoint, payload).then(response => {
                        if(!objectHelper.isEmptyObject(response.data)) {
                            this.subTotalPrice = response.data.subTotalPrice;
                            this.totalPrice = response.data.totalPrice;
                            this.discount = response.data.discount;
                            this.currency = response.data.currency;
                            this.cartItems = response.data.items;
                        } else {
                            this.subTotalPrice = 0;
                            this.totalPrice = 0;
                            this.discount = 0;
                            this.cartItems = [];
                            this.currency = {
                                name: null,
                                code: null,
                                symbol: null
                            };
                        }
                    }).catch(error => {
                        this.subTotalPrice = 0;
                        this.totalPrice = 0;
                        this.discount = 0;
                        this.cartItems = [];
                        this.currency = {
                            name: null,
                            code: null,
                            symbol: null
                        };
                        if (!error.response?.isCancel && (!error.name === 'AbortError' || !error.name === 'CanceledError') && (!error.message?.includes('aborted') || !error.message?.includes('canceled'))) {
                            errorHelper.error(error);
                        }
                    }).finally(() => {
                        if (_loader) {
                            elementHelper.loader.hide(loaderId);
                        }
                        if(element) {
                            element.classList.remove('position-relative');
                        }
                        this.loaded = true;
                    })
                });
            }
        },
        editItem(item) {
            if(this.isStandalone) {
                if(item.available) {
                    window.location.href = item.updateLink;
                }
            } else {
                this.$emit('changeBooking', item.props);
            }
        }
    }
}
</script>

<style scoped>

</style>
